import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { DiscountBadge } from 'QuoteApp/components/DiscountBadge';

import { Button, Modal } from '@clearcover/atlas';
import { FormInput, NumericFont } from 'Shared/components';
import {
  PaymentFrequencyType,
  PaymentPlanType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';

import { PaymentScheduleModalContent } from '../../PaymentScheduleModal';
import { getPaymentPlanDisplayName } from '../../utils';
import styles from './styles.module.css';

type PaymentFrequencyProps = {
  paymentFrequency: PaymentFrequencyType;
  paymentPlans: PaymentPlanType[];
  isSelected: boolean;
  isDisabled: boolean;
  isOnlyPaymentFrequency: boolean;
  isHidden?: boolean;
  onChecked: (paymentPlan: PaymentPlanType) => void;
};

type PaymentDescriptionProps = {
  frequency: PaymentFrequencyType;
  plan: PaymentPlanType;
};

const PaymentDescription: FC<PaymentDescriptionProps> = ({
  frequency,
  plan,
}) => {
  const [open, setOpen] = useState(false);
  if (frequency.numberOfPayments === 1) return <>Pay entire total cost today</>;

  return (
    <>
      {plan.downPaymentAmount} down payment +{` `}
      <div
        onClick={(event) => event.stopPropagation()}
        role="button"
        style={{ display: 'inline' }}
        tabIndex={0}
      >
        <Button
          link
          size="sm"
          className={styles.cleanLinkButton}
          onClick={() => setOpen(true)}
        >
          {`${frequency.numberOfPayments - 1} additional payments`}
        </Button>
        <Modal
          controlled={true}
          open={open}
          variant="normal"
          handleClose={() => setOpen(false)}
        >
          {(closeModal) => (
            <PaymentScheduleModalContent
              paymentPlan={plan}
              closeModal={closeModal}
            />
          )}
        </Modal>
      </div>
    </>
  );
};

export const PaymentFrequency: FC<PaymentFrequencyProps> = ({
  paymentFrequency,
  paymentPlans,
  isSelected,
  isDisabled,
  isHidden,
  isOnlyPaymentFrequency,
  onChecked,
}) => {
  const paymentPlan = paymentPlans.find(
    (plan) => plan.identifier === paymentFrequency.paymentPlanId
  );

  const getPaymentAmount = useCallback(
    (frequency: PaymentFrequencyType) => {
      const plan = paymentPlans.find(
        (p: PaymentPlanType) => p.identifier === frequency.paymentPlanId
      );

      if (!plan) return;

      if (frequency.numberOfPayments === 1) return plan.installmentAmount;

      const recurringPaymentAmount = plan.paymentSchedule[1].amount;
      return recurringPaymentAmount;
    },
    [paymentPlans]
  );

  const getPaymentAmountLabel = useCallback(
    (frequency: PaymentFrequencyType) => {
      const plan = paymentPlans.find(
        (p: PaymentPlanType) => p.identifier === frequency.paymentPlanId
      );

      if (!plan) return;

      if (frequency.numberOfPayments === 1) return 'Total Cost';

      const isSemiMonthlyPayPlan = frequency.identifier === 'semi_monthly';

      if (isSemiMonthlyPayPlan) {
        return 'Twice Per Month';
      }

      return 'Per Month';
    },
    [paymentPlans]
  );

  if (!paymentPlan || isHidden) return null;

  const paymentFrequencyDiscount =
    paymentPlan.discounts.filter((d) => d.type === 'paymentFrequencyDiscount')
      .length > 0;

  const label = (
    <>
      <div className={styles.labelHeader}>
        <span
          className={classNames({
            [styles.selected]: isSelected,
          })}
        >
          {getPaymentPlanDisplayName(paymentFrequency)}
        </span>
        {paymentFrequencyDiscount && (
          <DiscountBadge className={styles.discountBadge}>
            Discount Available
          </DiscountBadge>
        )}
      </div>
      <span className={styles.labelDetail}>
        <PaymentDescription frequency={paymentFrequency} plan={paymentPlan} />
      </span>
    </>
  );

  return (
    <div className={styles.borderContainer}>
      <div
        className={
          isOnlyPaymentFrequency ? styles.labelWrapper : styles.inputWrapper
        }
      >
        {isOnlyPaymentFrequency ? (
          <div
            data-testid={`frequency-only-${paymentPlan.paymentFrequencyIdentifier}-${paymentFrequency.paymentPlanEnum}`}
          >
            {label}
          </div>
        ) : (
          <FormInput
            className={styles.radioInput}
            label={label}
            variant="radio"
            dataTestIdPrefix={`frequency-${paymentPlan.paymentFrequencyIdentifier}-${paymentFrequency.paymentPlanEnum}`}
            name="frequency"
            value={paymentFrequency.paymentPlanId || ''}
            checked={isSelected}
            onChecked={() => {
              onChecked(paymentPlan);
            }}
            disabled={isDisabled}
          />
        )}
      </div>
      <div className={styles.paymentAmountWrapper}>
        <NumericFont
          className={styles.paymentAmount}
          value={getPaymentAmount(paymentFrequency)}
        />
        <span className={styles.labelDetail}>
          {getPaymentAmountLabel(paymentFrequency)}
        </span>
      </div>
    </div>
  );
};
